import React, { createContext, useReducer, useMemo, useCallback, useContext, useState } from 'react'
import { useStatusOverlay, useOverlay } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { initialMemoryBookState, memoryBooksReducer } from './MemoryBooksReducer'
import {
  deletePageAPI,
  postBlankBookPages,
  useGetFirstBook,
  updatePageOrderAPI,
  updatePageLayoutAPI,
  getBookById,
} from '../services/MemoryBooksAPI/memoryBooksAPI'
import TemplateBlueprints from '../components/PageAssembler/TemplateBlueprints'
import { getMemory } from '../services/MemoriesAPI/memoriesAPI'

const MemoryBookContext = createContext()

const normalizePageId = (value) => {
  if (value === 'front' || value === 'back') {
    return value
  }

  if (typeof value === 'number') {
    return value
  }

  const parsed = Number(value)
  return Number.isNaN(parsed) ? value : parsed
}

const buildPagePayload = (page) => ({
  templateId: page.templateId,
  elements: page.elements,
  stickers: page.stickers,
  customLayoutElements: page.customLayoutElements,
  backgroundId: page.backgroundId,
})

export function useMemoryBook() {
  const context = useContext(MemoryBookContext)

  if (!context) {
    throw new Error('useMemoryBook must be used within a MemoryBookProvider')
  }
  return context
}

export function MemoryBookProvider({ children, siteConfig, isAuthenticated = false }) {
  const [t] = useTranslation()
  const showStatusOverlay = useStatusOverlay()
  const initialState = useMemo(() => initialMemoryBookState(), [])
  const [memoryBook, dispatch] = useReducer(memoryBooksReducer, initialState)
  const { bookId: initialBookId, error, loading } = useGetFirstBook(dispatch, isAuthenticated)

  // Use the bookId from memoryBook state (updated when loadBook is called) or fall back to initialBookId
  const bookId = memoryBook?.id || initialBookId

  // Add a page to the book. You can specify how many pages to add as well as where to add them.
  const addBookPages = useCallback(
    async (numOfPagesToAdd = 1, addAfterThisPageNum = memoryBook.contentPages.length - 2, isMobile = false) => {
      if (!bookId) {
        console.error('Book ID is not set. Could not add pages.')
        return
      }
      // If The user chooses nothing, just return out.
      const pagesToAdd = Number(numOfPagesToAdd) || 0

      if (pagesToAdd === 0) {
        return
      }

      const pageNum = Number(addAfterThisPageNum)

      const insertPageNum =
        pageNum >= 0 && pageNum < memoryBook.contentPages.length - 2 ? pageNum : memoryBook.contentPages.length - 2

      const onSuccess = (response) => {
        showStatusOverlay({
          message: t('MemoryBookProvider.successAddingPages.description', 'Added {pagesToAdd} page(s) to the book.', {
            pagesToAdd,
          }),
          type: 'success',
          dismissable: true,
        })
        dispatch({
          type: 'ADD_PAGE',
          payload: {
            numOfPagesToAdd: pagesToAdd,
            addAfterThisPageNum: insertPageNum,
            isMobile,
            response: response.data,
          },
        })
      }

      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemAddingPages.description',
            'There was a problem adding the pages, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }

      await postBlankBookPages({
        bookId,
        startingPageNumber: pageNum,
        pageCount: pagesToAdd,
        onSuccess,
        onError,
      })
    },
    [bookId, memoryBook.contentPages.length, showStatusOverlay, t]
  )

  const deleteBookPage = useCallback(
    async (pageNumber, isMobile = false) => {
      const onSuccess = (response) => {
        showStatusOverlay({
          message: t('MemoryBookProvider.successDeletingPage.description', 'Successfully Deleted Page {pageNumber}', {
            pageNumber,
          }),
          type: 'success',
          dismissable: true,
        })
        dispatch({ type: 'DELETE_PAGE', payload: { pageNumber, isMobile, response: response.data } })
      }
      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemDeletingPage.description',
            'There was a problem deleting the page, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }
      deletePageAPI({
        bookId,
        pageNumber,
        onSuccess,
        onError,
      })
    },
    [bookId, showStatusOverlay, t]
  )

  const turnBookPage = useCallback(
    (direction, isMobile = false) => {
      dispatch({ type: 'TURN_PAGE', payload: { direction, isMobile } })
    },
    [dispatch]
  )

  const updatePageOrder = useCallback(
    (dropPageNumber, draggedIndexPosition) => {
      const draggedPageId = memoryBook.contentPages[draggedIndexPosition].id

      updatePageOrderAPI({
        bookId,
        page: draggedPageId,
        newPageNumber: dropPageNumber,
        onSuccess: (response) => {
          dispatch({ type: 'UPDATE_PAGE_ORDER', payload: { response: response.data } })
        },
      })
    },
    [memoryBook.contentPages, bookId, dispatch]
  )

  const updateViewingPages = useCallback(
    ({ pageNumber, isMobile = false }) => {
      dispatch({ type: 'UPDATE_VIEWING_PAGES', payload: { pageNumber, isMobile } })
    },
    [dispatch]
  )

  const updatePageLayout = useCallback(
    async (pageNumber, newTemplateId, props) => {
      const elements = TemplateBlueprints(newTemplateId, props)
      const onSuccess = (response) => {
        dispatch({ type: 'UPDATE_PAGE_LAYOUT', payload: { pageNumber, response: response.data } })
      }
      updatePageLayoutAPI({
        bookId,
        templateId: newTemplateId,
        page: pageNumber,
        elements,
        onSuccess,
      })
    },
    [bookId]
  )

  const updatePageContent = useCallback(
    async (pageNumber, positionIndex, updates = {}, retryCount = 0) => {
      const MAX_RETRIES = 5
      if (!bookId) {
        if (retryCount < MAX_RETRIES) {
          setTimeout(async () => {
            // Retry the function after a delay
            await updatePageContent(pageNumber, positionIndex, updates, retryCount + 1)
          }, 100)
        }
        return
      }

      const normalizedPageNumber = normalizePageId(pageNumber)
      const selectedPage = memoryBook.contentPages.find((page) => normalizePageId(page.page) === normalizedPageNumber)
      if (!selectedPage) {
        return
      }
      const selectedElement = selectedPage.elements[positionIndex]

      // Ensure element has props object (safety check for legacy data)
      if (!selectedElement.props) {
        selectedElement.props = {}
      }

      // Only check properties that are being updated
      const updateNeeded = Object.keys(updates).some((key) => {
        if (key === 'memoryId') {
          return selectedElement.props.memoryId !== updates.memoryId
        }
        if (key === 'caption') {
          return selectedElement.props.caption !== updates.caption
        }
        if (key === 'title') {
          return selectedElement.props.title !== updates.title
        }
        if (key === 'cropArea') {
          const currentCropArea = selectedElement.props.cropArea || undefined
          const newCropArea = updates.cropArea ? JSON.stringify(updates.cropArea) : undefined
          return currentCropArea !== newCropArea
        }
        return false
      })

      const updatePageElements = (passedPage) => {
        const element = passedPage.elements[positionIndex]

        if (!element.props) {
          element.props = {}
        }

        if (element.type === 'text') {
          if (updates.memoryId !== undefined) {
            element.props.memoryId = updates.memoryId
          }
        } else {
          const updatedProps = { ...element.props }

          if (updates.memoryId !== undefined) {
            updatedProps.memoryId = updates.memoryId
          }
          if (updates.caption !== undefined) {
            updatedProps.caption = updates.caption
          }
          if (updates.title !== undefined) {
            updatedProps.title = updates.title
          }
          if (updates.cropArea !== undefined) {
            updatedProps.cropArea = updates.cropArea ? JSON.stringify(updates.cropArea) : undefined
          }

          element.props = updatedProps
        }

        passedPage.elements[positionIndex] = element
        return passedPage.elements
      }

      const onSuccess = () => {
        if (selectedElement.type === 'text') {
          const payload = {
            pageNumber,
            positionIndex,
          }
          if (updates.memoryId !== undefined) {
            payload.updatedId = updates.memoryId
          }
          dispatch({
            type: 'UPDATE_PAGE_CONTENT',
            payload,
          })
        } else if (selectedElement.type === 'image' || selectedElement.type === 'backPage') {
          const payload = {
            pageNumber: normalizedPageNumber,
            positionIndex,
          }
          if (updates.memoryId !== undefined) {
            payload.updatedId = updates.memoryId
          }
          if (updates.caption !== undefined) {
            payload.caption = updates.caption
          }
          if (updates.title !== undefined) {
            payload.title = updates.title
          }
          if (updates.cropArea !== undefined) {
            payload.cropArea = updates.cropArea
          }
          dispatch({
            type: 'UPDATE_PAGE_CONTENT',
            payload,
          })
        }
      }

      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemUpdatingPages.description',
            'There was a problem updating the page, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }

      if (updateNeeded) {
        await updatePageLayoutAPI({
          bookId,
          page: normalizedPageNumber,
          ...buildPagePayload(selectedPage),
          elements: updatePageElements(selectedPage),
          onSuccess,
          onError,
        })
      }
    },
    [bookId, memoryBook.contentPages, showStatusOverlay, t]
  )

  const updatePageStickers = useCallback(
    async (pageNumber, stickers = [], retryCount = 0) => {
      const MAX_RETRIES = 5

      if (!bookId) {
        if (retryCount < MAX_RETRIES) {
          setTimeout(async () => {
            await updatePageStickers(pageNumber, stickers, retryCount + 1)
          }, 100)
        }
        return
      }

      const normalizedPageNumber = normalizePageId(pageNumber)
      const selectedPage = memoryBook.contentPages.find((page) => normalizePageId(page.page) === normalizedPageNumber)
      if (!selectedPage) {
        return
      }

      const sanitizedStickers = Array.isArray(stickers)
        ? stickers
            .filter((sticker) => sticker?.id)
            .map((sticker) => ({
              ...sticker,
              dimensions: { ...sticker.dimensions },
            }))
        : []

      const existingStickers = Array.isArray(selectedPage.stickers) ? selectedPage.stickers : []

      const existingSerialized = JSON.stringify(existingStickers)
      const nextSerialized = JSON.stringify(sanitizedStickers)

      if (existingSerialized === nextSerialized) {
        return
      }

      const onSuccess = () => {
        dispatch({
          type: 'UPDATE_PAGE_STICKERS',
          payload: { pageNumber: normalizedPageNumber, stickerState: sanitizedStickers },
        })
      }

      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemUpdatingStickers.description',
            'There was a problem updating stickers, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }

      await updatePageLayoutAPI({
        bookId,
        page: normalizedPageNumber,
        ...buildPagePayload(selectedPage),
        stickers: sanitizedStickers,
        onSuccess,
        onError,
      })
    },
    [bookId, memoryBook.contentPages, showStatusOverlay, t]
  )

  const updatePageCustomLayoutElements = useCallback(
    async (pageNumber, customLayoutElements = [], retryCount = 0) => {
      const MAX_RETRIES = 5

      if (!bookId) {
        if (retryCount < MAX_RETRIES) {
          setTimeout(async () => {
            await updatePageCustomLayoutElements(pageNumber, customLayoutElements, retryCount + 1)
          }, 100)
        }
        return
      }

      const normalizedPageNumber = normalizePageId(pageNumber)
      const selectedPage = memoryBook.contentPages.find((page) => normalizePageId(page.page) === normalizedPageNumber)
      if (!selectedPage) {
        return
      }

      const sanitizedElements = Array.isArray(customLayoutElements)
        ? customLayoutElements
            .filter((element) => element?.id)
            .map((element) => ({
              ...element,
              dimensions: element.dimensions ? { ...element.dimensions } : undefined,
            }))
        : []

      const existingElements = Array.isArray(selectedPage.customLayoutElements) ? selectedPage.customLayoutElements : []

      const existingSerialized = JSON.stringify(existingElements)
      const nextSerialized = JSON.stringify(sanitizedElements)

      if (existingSerialized === nextSerialized) {
        return
      }

      const onSuccess = () => {
        dispatch({
          type: 'UPDATE_PAGE_CUSTOM_LAYOUT_ELEMENTS',
          payload: { pageNumber: normalizedPageNumber, customLayoutElements: sanitizedElements },
        })
      }

      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemUpdatingCustomLayoutElements.description',
            'There was a problem updating layout elements, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }

      await updatePageLayoutAPI({
        bookId,
        page: normalizedPageNumber,
        ...buildPagePayload(selectedPage),
        customLayoutElements: sanitizedElements,
        onSuccess,
        onError,
      })
    },
    [bookId, memoryBook.contentPages, showStatusOverlay, t]
  )

  const updateCustomLayoutElementData = useCallback(
    async (pageNumber, elementId, updates = {}) => {
      const normalizedPageNumber = normalizePageId(pageNumber)
      const selectedPage = memoryBook.contentPages.find((page) => normalizePageId(page.page) === normalizedPageNumber)

      if (!selectedPage) {
        return
      }

      const existingElements = Array.isArray(selectedPage.customLayoutElements) ? selectedPage.customLayoutElements : []
      const element = existingElements.find((el) => el?.id === elementId)

      if (!element) {
        return
      }

      // Check if update is needed
      const currentPickerData = element.pickerData || {}
      const updateNeeded = Object.keys(updates).some((key) => {
        if (key === 'cropArea') {
          const currentCropArea = currentPickerData.cropArea || undefined
          const newCropArea = updates.cropArea ? JSON.stringify(updates.cropArea) : undefined
          return currentCropArea !== newCropArea
        }
        return currentPickerData[key] !== updates[key]
      })

      if (!updateNeeded) {
        return
      }

      // Create updated elements array
      const updatedElements = existingElements.map((el) => {
        if (el.id !== elementId) {
          return el
        }

        const updatedPickerData = { ...currentPickerData }

        Object.keys(updates).forEach((key) => {
          if (key === 'cropArea') {
            updatedPickerData.cropArea = updates.cropArea ? JSON.stringify(updates.cropArea) : undefined
          } else {
            updatedPickerData[key] = updates[key]
          }
        })

        return {
          ...el,
          pickerData: updatedPickerData,
        }
      })

      await updatePageCustomLayoutElements(pageNumber, updatedElements)
    },
    [memoryBook.contentPages, updatePageCustomLayoutElements]
  )

  const updatePageBackground = useCallback(
    async function handleUpdatePageBackground(pageNumber, backgroundId, retryCount = 0) {
      const MAX_RETRIES = 5

      if (!bookId) {
        if (retryCount < MAX_RETRIES) {
          setTimeout(async () => {
            await handleUpdatePageBackground(pageNumber, backgroundId, retryCount + 1)
          }, 100)
        }
        return
      }

      const normalizedPageNumber = normalizePageId(pageNumber)
      const selectedPage = memoryBook.contentPages.find((page) => normalizePageId(page.page) === normalizedPageNumber)

      if (!selectedPage) {
        return
      }

      const nextBackgroundId = backgroundId ?? null
      const currentBackgroundId = selectedPage.backgroundId ?? null

      if (currentBackgroundId === nextBackgroundId) {
        return
      }

      const onSuccess = () => {
        dispatch({
          type: 'UPDATE_PAGE_BACKGROUND',
          payload: { pageNumber: normalizedPageNumber, backgroundId: nextBackgroundId },
        })
      }

      const onError = () => {
        showStatusOverlay({
          message: t(
            'MemoryBookProvider.problemUpdatingBackground.description',
            'There was a problem updating the background, please try again later.'
          ),
          type: 'error',
          dismissable: true,
        })
      }

      await updatePageLayoutAPI({
        bookId,
        page: normalizedPageNumber,
        ...buildPagePayload(selectedPage),
        backgroundId: nextBackgroundId,
        onSuccess,
        onError,
      })
    },
    [bookId, memoryBook.contentPages, showStatusOverlay, t]
  )

  const updateMobileView = useCallback((isMobile) => {
    dispatch({ type: 'SET_MOBILE_MODE', payload: { isMobile } })
  }, [])

  const loadBook = useCallback(
    async (requestedBookId) => {
      if (!requestedBookId) {
        console.error('No bookId provided to loadBook')
        return
      }

      try {
        const { bookData, error: fetchError } = await getBookById(requestedBookId)

        if (fetchError) {
          console.error('Error loading book:', fetchError)
          showStatusOverlay({
            message: t('MemoryBookProvider.errorLoadingBook', 'Failed to load book. Please try again.'),
            type: 'error',
            dismissable: true,
          })
          return
        }

        if (!bookData) {
          console.error('Book not found:', requestedBookId)
          showStatusOverlay({
            message: t('MemoryBookProvider.bookNotFound', 'Book not found. It may have been deleted.'),
            type: 'error',
            dismissable: true,
          })
          return
        }

        dispatch({ type: 'SET_BOOK', payload: bookData })
      } catch (err) {
        console.error('Exception in loadBook:', err)
        showStatusOverlay({
          message: t('MemoryBookProvider.errorLoadingBook', 'Failed to load book. Please try again.'),
          type: 'error',
          dismissable: true,
        })
      }
    },
    [dispatch, showStatusOverlay, t]
  )

  const memoryViewerOverlay = useOverlay()
  const [memoryViewerMemoryId, setMemoryViewerMemoryId] = useState(null)

  const memoryLoader = useCallback(async () => {
    if (!memoryViewerMemoryId) return []
    const response = await getMemory(memoryViewerMemoryId)

    if (!response) {
      return []
    }

    return Array.isArray(response) ? response : [response]
  }, [memoryViewerMemoryId])

  const values = useMemo(() => {
    return {
      memoryBook,
      currentViewingPages: memoryBook.currentPages,
      addBookPages,
      deleteBookPage,
      turnBookPage,
      updatePageOrder,
      updateViewingPages,
      updatePageLayout,
      updatePageContent,
      updatePageStickers,
      updatePageCustomLayoutElements,
      updateCustomLayoutElementData,
      updatePageBackground,
      updateMobileView,
      loadBook,
      isError: error,
      isLoading: loading,
      memoryViewerOverlay,
      memoryViewerMemoryId,
      setMemoryViewerMemoryId,
      memoryLoader,
      siteConfig,
    }
  }, [
    memoryBook,
    addBookPages,
    deleteBookPage,
    turnBookPage,
    updatePageOrder,
    updateViewingPages,
    updatePageLayout,
    updatePageContent,
    updatePageStickers,
    updatePageCustomLayoutElements,
    updateCustomLayoutElementData,
    updatePageBackground,
    updateMobileView,
    loadBook,
    error,
    loading,
    memoryViewerOverlay,
    memoryViewerMemoryId,
    memoryLoader,
    siteConfig,
  ])

  return <MemoryBookContext.Provider value={values}>{children}</MemoryBookContext.Provider>
}
